<template>
  <td :class="['zone-column', { 'service-column': isServiceProduct }]">
    {{ zone.displayName }}
  </td>
  <td :class="['carton-price-column', { 'service-column': isServiceProduct }]">
    {{ $filters.currency(zone.cartonPrice) }}
  </td>
  <td :class="['amount-off-column', { 'service-column': isServiceProduct }]">
    <v-form :ref="`formAmountOff${zone.code}`" @submit.prevent>
      <i-numeric-field
        dense
        hide-details="auto"
        outlined
        :min="0"
        :max="amountOffMax"
        :precision="2"
        :prefix="amountOffPrefix"
        :suffix="amountOffSuffix"
        :empty-zero="isPercentageDiscountMode"
        :label="amountOffLabel"
        :background-color="backgroundColor"
        :disabled="isCalculatingProductPriceForRegion(zone.code)"
        :loading="isCalculatingProductPriceForRegion(zone.code)"
        :rules="[
          alertOnEmptyRowFields(
            zone.amountOff,
            isServiceProduct ? [] : [zone.unitRrp]
          ),
          rulesSpecialAmountOff(zone.amountOff, zone.cartonPrice)
        ]"
        text-align-right
        :value="amountOffInputValue(zone)"
        @input="onAmountOffInput(zone, $event)"
      >
      </i-numeric-field>
      <div
        v-if="isPercentageDiscountMode && zone.amountOff"
        class="amount-off-helper"
      >
        Amount off: {{ $filters.currency(zone.amountOff) }}
      </div>
    </v-form>
  </td>
  <td
    :class="[
      'row-cell',
      'revised-unit-ex-tax-column',
      'i-td-chip',
      { 'service-column': isServiceProduct }
    ]"
  >
    <v-chip class="chip">
      <template v-if="variableWeightProductSelected">
        {{ $filters.currency(zone.revisedCostPerKGIncTax) }}
      </template>
      <template v-else>{{
        $filters.currency(zone.revisedUnitPriceExTax)
      }}</template>
    </v-chip>
  </td>
  <td
    v-if="!isUSCountry"
    :class="[
      'row-cell',
      'revised-unit-inc-tax-column',
      'text-center',
      { 'service-column': isServiceProduct }
    ]"
  >
    <template v-if="variableWeightProductSelected">
      {{ $filters.currency(zone.revisedCostPerKGIncTax) }}
    </template>
    <template v-else>{{
      $filters.currency(zone.revisedUnitPriceIncTax)
    }}</template>
  </td>
  <td
    v-if="!isServiceProduct && !isUSCountry"
    class="row-cell original-rrp-column text-only"
  >
    {{ $filters.currency(getOriginalProductRrp(zone)) }}
  </td>
  <td
    v-if="!isServiceProduct && !isUSCountry"
    class="row-cell special-unit-column"
  >
    <v-form :ref="`formSpecialUnitRrp${zone.code}`" @submit.prevent>
      <i-numeric-field
        dense
        hide-details="auto"
        outlined
        :min="0"
        :max="9999.99"
        :precision="2"
        :prefix="currencySymbol"
        :background-color="backgroundColor"
        :disabled="isCalculatingProductPriceForRegion(zone.code)"
        :loading="isCalculatingProductPriceForRegion(zone.code)"
        :label="
          variableWeightProductSelected
            ? 'Special RRP / KG'
            : 'Special Unit RRP'
        "
        :rules="[
          alertOnEmptyRowFields(zone.unitRrp, [zone.amountOff]),
          rulesSpecialUnitRrp(
            zone.unitRrp,
            zone.grossProfitPercentage,
            variableWeightProductSelected
          )
        ]"
        text-align-right
        v-model="zone.unitRrp"
        @input="(calcPricingForZone(zone), onZoneFieldChange(zone.code))"
      >
      </i-numeric-field>
    </v-form>
  </td>
  <td
    v-if="!isServiceProduct && !isUSCountry"
    class="row-cell i-td-chip gpp-column"
  >
    <v-chip
      class="chip"
      :class="{
        negative: zone.grossProfitPercentage < 0,
        positive: zone.grossProfitPercentage > 0
      }"
    >
      {{ zone.grossProfitPercentage || '-' }}
    </v-chip>
  </td>
</template>
<script>
import FieldValidators from '@/utils/FieldValidators'
import iNumericField from '@/components/NumericField'
import { calculateProductPrice } from '@/services/products'
import { mapGetters } from 'vuex'
import { buildCountryDisplayRules } from '@/utils/countryDisplay'
import {
  amountOffFromDiscountPercentage,
  discountPercentageFromAmountOff,
  DISCOUNT_ENTRY_MODE,
  formatDiscountPercentage,
  setDiscountPercentageFromAmountOff
} from '@/modules/dataMaintenance/helper/DiscountEntryHelper'

const DEBOUNCE_CALC_TIMER = 1500

export default {
  components: {
    iNumericField
  },

  data: () => ({
    backgroundColor: '#0000000f',
    calculatingProductPriceRegionCodes: [],
    debounceCalcProductPrice: [],
    fields: {},
    ...FieldValidators
  }),

  props: {
    error: {
      type: Boolean,
      default: false
    },
    isDuplicate: Boolean,
    isServiceProduct: {
      type: Boolean,
      default: false
    },
    selectedProduct: Object,
    zone: Object,
    discountEntryMode: {
      type: String,
      default: DISCOUNT_ENTRY_MODE.AMOUNT
    }
  },

  computed: {
    ...mapGetters('tenant', [
      'tenantCurrency',
      'tenantLocale',
      'tenantCountryRegion'
    ]),

    currencySymbol() {
      return (
        new Intl.NumberFormat(this.tenantLocale, {
          style: 'currency',
          currency: this.tenantCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value || '$'
      )
    },

    variableWeightProductSelected() {
      return (
        !!this.selectedProduct &&
        !!this.selectedProduct.variableWeightTargetInKg
      )
    },

    displayRules() {
      return buildCountryDisplayRules(this.tenantCountryRegion)
    },

    isUSCountry() {
      return this.displayRules.isUSCountry
    },

    isPercentageDiscountMode() {
      return this.discountEntryMode === DISCOUNT_ENTRY_MODE.PERCENTAGE
    },

    amountOffLabel() {
      return this.isPercentageDiscountMode ? 'Discount %' : 'Amount Off'
    },

    amountOffPrefix() {
      return this.isPercentageDiscountMode ? '' : this.currencySymbol
    },

    amountOffSuffix() {
      return this.isPercentageDiscountMode ? '%' : undefined
    },

    amountOffMax() {
      return this.isPercentageDiscountMode ? 99.99 : 9999.99
    }
  },

  methods: {
    amountOffInputValue(zone) {
      if (!this.isPercentageDiscountMode) return zone.amountOff
      if (zone.discountPercentage) return zone.discountPercentage
      return formatDiscountPercentage(
        discountPercentageFromAmountOff(zone.cartonPrice, zone.amountOff),
        this.tenantLocale
      )
    },

    onAmountOffInput(zone, value) {
      if (this.isPercentageDiscountMode) {
        zone.discountPercentage = value
        zone.amountOff = amountOffFromDiscountPercentage(
          zone.cartonPrice,
          value
        )
      } else {
        zone.amountOff = value
        setDiscountPercentageFromAmountOff(zone, zone.cartonPrice, value)
      }
      this.calcPricingForZone(zone)
      this.onZoneFieldChange(zone.code)
    },

    getOriginalProductRrp(zone) {
      const zoneOriginalRrp = Number(zone?.originalUnitRrp)
      if (!isNaN(zoneOriginalRrp) && zoneOriginalRrp > 0) return zoneOriginalRrp

      const fallbackRrp = Number(
        this.selectedProduct?.regionalPriceMap?.[zone.code]?.[0]
          ?.regionUnitRrp ||
          this.selectedProduct?.regionalPriceMap?.[zone.code]?.[0]?.rrp
      )
      return !isNaN(fallbackRrp) && fallbackRrp > 0 ? fallbackRrp : null
    },

    isCalculatingProductPriceForRegion(pricingRegionCode) {
      return (
        this.calculatingProductPriceRegionCodes.indexOf(pricingRegionCode) !==
        -1
      )
    },

    calcPricingForZone(zone) {
      if (zone.amountOff) {
        this.debounceCalcPricingForZone(zone)
      } else {
        zone.grossProfitPercentage = null
        zone.revisedUnitPriceExTax = null
        zone.revisedUnitPriceIncTax = null
        zone.revisedCostPerKGExTax = null
        zone.revisedCostPerKGIncTax = null
      }
    },

    debounceCalcPricingForZone(zone) {
      const regionCode = zone.code
      clearTimeout(this.debounceCalcProductPrice[regionCode])
      this.debounceCalcProductPrice[regionCode] = setTimeout(() => {
        if (
          this.calculatingProductPriceRegionCodes.indexOf(regionCode) === -1
        ) {
          this.calculatingProductPriceRegionCodes.push(regionCode)
        }

        calculateProductPrice({
          cartonPriceExTax: zone.cartonPrice - zone.amountOff,
          packageQuantity: zone.packageQuantity,
          variableWeightTargetInKg: zone.variableWeightTargetInKg,
          // srpIncTax is the same as unitRrp
          srpIncTax: zone.unitRrp || null,
          hasGst: zone.hasGst,
          hasWet: zone.hasWet
        }).then((data) => {
          zone.grossProfitPercentage = data.gpp
          zone.revisedUnitPriceExTax = data.unitPriceExTax
          zone.revisedUnitPriceIncTax = data.unitPriceIncTax
          zone.revisedCostPerKGExTax = data.pricePerKgExTax
          zone.revisedCostPerKGIncTax = data.pricePerKgIncTax
          if (this.isServiceProduct) {
            zone.unitRrp = data.unitPriceIncTax
            zone.grossProfitPercentage = null
          }
          this.removeCalculatingProductPriceRegionCode(regionCode)
        })
      }, DEBOUNCE_CALC_TIMER)
    },

    removeCalculatingProductPriceRegionCode(regionCode) {
      const regionCodeIndex =
        this.calculatingProductPriceRegionCodes.indexOf(regionCode)
      if (regionCodeIndex !== -1) {
        this.calculatingProductPriceRegionCodes.splice(regionCodeIndex, 1)
      }
    },

    onZoneFieldChange(zoneCode) {
      /*
        For each zone, a form wraps the v-text-field element.
        On field change, we trigger form validation manually as
        :rules validator is not reactive unless field clicked first
        in our case we need reactivity even if field not clicked yet
      */
      this.$nextTick(() => {
        this.$refs[`formAmountOff${zoneCode}`].validate()
        if (
          !this.isServiceProduct &&
          !this.isUSCountry &&
          this.$refs[`formSpecialUnitRrp${zoneCode}`]
        ) {
          this.$refs[`formSpecialUnitRrp${zoneCode}`].validate()
        }
      })
    },

    handleZonePricingInput(zone) {
      this.calcPricingForZone(zone)
      this.onZoneFieldChange(zone.code)
    }
  }
}
</script>
<style lang="scss" scoped>
.amount-off-helper {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  line-height: 16px;
  margin-top: 2px;
  text-align: right;
}
</style>
<style lang="scss" scoped>
td {
  height: 75px !important;
  padding: 10px 0 !important;
  vertical-align: middle;
  text-align: center;
}
.zone-column,
.carton-price-column,
.revised-unit-ex-tax-column,
.revised-unit-inc-tax-column,
.original-rrp-column,
.gpp-column {
  width: 12.5% !important;
}
.amount-off-column,
.special-unit-column {
  width: 12.5% !important;
}
.service-column {
  width: 20% !important;
}
</style>
